import appNavItemTypeCheckers from 'types/appNavItemTypeCheckers';

export interface BonusAppSignOffBoard {
  // Stable, unique key for React lists (the nav item key, or a synthesised key
  // for Approvals tabs which have no key of their own).
  key: string;
  label: string;
  boardId: string;
  // The underlying nav item or Approvals tab this board came from.
  navItem: V3.Navigation.SignOffBoard | V3.Navigation.ApprovalsSignOffBoardTab;
  // Present only when the board is configured as an Approvals tab. Used to
  // deep-link to the Approvals screen with the correct tab selected — the tabId
  // format mirrors useApprovalsTabs (`${tab.type}-${index}`).
  approvals?: {
    approvalsKey: string;
    tabId: string;
  };
}

/**
 * Enumerates every sign-off board configured on a bonus app, from BOTH sources:
 *  - top-level `Sign Off Board` nav items, and
 *  - `sign-off-board` tabs nested under an `Approvals` nav item (the newer nested
 *    format used by sub-period / WEL apps).
 *
 * Both the per-board Sign Off button (SignOffBoardNavItemProvider) and the
 * close-period checklist (BoardSignOffItem) must agree on this set, otherwise the
 * checklist silently counts 0 boards for Approvals-tab apps and lets a period be
 * closed with nothing signed off. Top-level nav items are listed first (matching
 * the priority used when resolving a single board), then Approvals tabs.
 */
const getBonusAppSignOffBoards = (app: V3.BonusApp): BonusAppSignOffBoard[] => {
  const navItemBoards: BonusAppSignOffBoard[] = [];
  const approvalsTabBoards: BonusAppSignOffBoard[] = [];

  app.navigationSettings.sections.forEach((section) => {
    section.items.forEach((item) => {
      if (appNavItemTypeCheckers.isSignOffBoard(item)) {
        navItemBoards.push({
          key: item.key,
          label: item.label,
          boardId: item.boardId,
          navItem: item,
        });
      } else if (appNavItemTypeCheckers.isApprovals(item)) {
        item.tabs.forEach((tab, index) => {
          if (tab.type === 'sign-off-board') {
            approvalsTabBoards.push({
              key: `${item.key}-${tab.type}-${index}`,
              label: tab.label,
              boardId: tab.boardId,
              navItem: tab,
              approvals: {
                approvalsKey: item.key,
                tabId: `${tab.type}-${index}`,
              },
            });
          }
        });
      }
    });
  });

  return [...navItemBoards, ...approvalsTabBoards];
};

export default getBonusAppSignOffBoards;
